/**
 * As placas que as câmeras leram, para "adotar sugestão" em um clique (`C-11`, CA-03).
 *
 * Quando as vistas discordam, o operador compara e escolhe. Antes, escolher era redigitar a
 * placa de outra câmera, letra por letra — e a divergência mais comum é uma letra só (o `8`
 * que era `B`). Aqui cada placa distinta vira um botão, com quem a leu e a certeza; a melhor
 * vem primeiro.
 *
 * Duas fontes, as mesmas do painel: as leituras por câmera do pórtico (`leituras`) e as
 * vistas de um upload agrupado (`imagens_de_analise`). Placa igual lida por duas câmeras é
 * **uma** candidata, com as duas fontes — é justamente o que dá confiança nela.
 */
import type { DetalheDoTransito } from "../api/tipos";

export interface PlacaCandidata {
  placa: string;
  /** Quem leu: "frontal", "lateral", o nome da vista… */
  fontes: string[];
  /** A maior certeza entre as fontes, de 0 a 100; `null` se nenhuma disse. */
  confianca: number | null;
}

const NOME_DA_CAMERA: Record<string, string> = {
  FRONTAL: "frontal",
  TRASEIRA: "traseira",
  LATERAL: "lateral",
  EIXO: "eixo",
  PANORAMICA: "panorâmica",
};

function emPct(v: number | null | undefined): number | null {
  if (v == null || Number.isNaN(v)) return null;
  return Math.round(v <= 1 ? v * 100 : v);
}

export function placasCandidatas(
  detalhe: Pick<DetalheDoTransito, "leituras" | "imagens_de_analise">,
): PlacaCandidata[] {
  const lidas: { placa: string; fonte: string; confianca: number | null }[] =
    [];
  for (const l of detalhe.leituras ?? []) {
    if (l.placa)
      lidas.push({
        placa: l.placa,
        fonte: NOME_DA_CAMERA[l.camera] ?? l.camera.toLowerCase(),
        confianca: emPct(l.confianca),
      });
  }
  for (const v of detalhe.imagens_de_analise ?? []) {
    if (v.placa)
      lidas.push({
        placa: v.placa,
        fonte: v.vista || v.nome_original,
        confianca: emPct(v.placa_confianca),
      });
  }

  const porPlaca = new Map<string, PlacaCandidata>();
  for (const l of lidas) {
    const placa = l.placa.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const atual = porPlaca.get(placa);
    if (!atual) {
      porPlaca.set(placa, {
        placa,
        fontes: [l.fonte],
        confianca: l.confianca,
      });
    } else {
      if (!atual.fontes.includes(l.fonte)) atual.fontes.push(l.fonte);
      if (
        l.confianca !== null &&
        (atual.confianca === null || l.confianca > atual.confianca)
      )
        atual.confianca = l.confianca;
    }
  }
  // Mais fontes primeiro (duas câmeras concordando valem mais que uma certa), depois a certeza.
  return [...porPlaca.values()].sort(
    (a, b) =>
      b.fontes.length - a.fontes.length ||
      (b.confianca ?? -1) - (a.confianca ?? -1),
  );
}

/** O motivo que a adoção escreve sozinha — o operador pode mudar antes de aprovar. */
export function motivoDaAdocao(c: PlacaCandidata): string {
  const quem = c.fontes.join(" e ");
  const certeza = c.confianca === null ? "" : `, ${c.confianca}%`;
  return `Adotada a leitura da câmera ${quem} (${c.placa}${certeza}).`;
}
